/* OtterLabs — bundle entry point.
   ==============================

   THE BRIDGE, and the reason this migration can be done a piece at a time.

   app.html is ~27,000 lines of inline script that calls these functions by
   name, as globals. Rewriting all of that at once to use imports would be a
   rewrite, and rewrites of untested code are how projects die. So instead the
   bundle puts everything back on `window`, exactly where the inline code
   already expects to find it, and the inline code does not change at all.

   That means each extraction is verifiable on its own: move a function out,
   re-export it here, and if the app still works the move was correct. The
   alternative — a big-bang split — gives you one enormous change that either
   works or does not, with no way to tell which piece broke it.

   The bundle is loaded from <head> with a plain <script src>, so it runs before
   any inline block. No SRI hash: this file is same-origin and built from this
   repository. SRI exists here to defend against a compromised CDN, and there
   is no CDN in this path.

   As modules are extracted, they get added here. When enough of app.html has
   moved out that the inline blocks are thin, the bridge can start shrinking —
   but not before, and there is no hurry.
*/

import * as time from './lib/time.js';
import * as format from './lib/format.js';
import * as dates from './lib/dates.js';
import * as csv from './lib/csv.js';
import * as ids from './lib/ids.js';
import * as text from './lib/text.js';
import * as assign from './lib/assign.js';
import * as mhMatch from './lib/mh-match.js';
import './features/share-dialog.js';    // each self-registers via onReady
import './features/doc-editor.js';
import './features/notebook.js';
import './features/whiteboard.js';
import './features/ai-panel.js';
import './features/recorder.js';
/* Meeting Mode gates its own wiring, so it is imported for its side effects
   and publishes otterAI() — which the recorder and the AI panel both use. */
import './features/meeting-mode.js';

/* Assigned individually rather than via Object.assign(window, time) so the
   list of globals this file creates is greppable. If a name here ever collides
   with something still defined inline, the inline one wins — it is parsed
   later — which would be silent and confusing, so keep this list honest. */
window.mhWeekStartKey = time.mhWeekStartKey;
window.mhComputeMinutes = time.mhComputeMinutes;
window.mhComputeUnits = time.mhComputeUnits;
window.mhFormatMMDDYY = time.mhFormatMMDDYY;
window.mhFormatTime12 = time.mhFormatTime12;
window.mhTimeToMin = time.mhTimeToMin;
window.mhMinToTime = time.mhMinToTime;
window.mhAddMinutes = time.mhAddMinutes;

window.escapeHtml = format.escapeHtml;
window.escapeAttr = format.escapeAttr;
window.todayISO = format.todayISO;
window.prettyDate = format.prettyDate;

window.daysBetween = dates.daysBetween;
window.addMonthsISO = dates.addMonthsISO;
window.isBirthdayToday = dates.isBirthdayToday;
window.relativeTime = dates.relativeTime;
window.parseCSVDate = dates.parseCSVDate;
window.expiresInDays = dates.expiresInDays;
window.TRASH_RETENTION_DAYS = dates.TRASH_RETENTION_DAYS;

window.csvEscape = csv.csvEscape;
window.buildCSV = csv.buildCSV;
window.findHeader = csv.findHeader;
/* Renamed on the way out — it is not Member Hours specific. The old name is
   kept here so the call sites in app.html did not have to change. */
window.mhTruthy = csv.csvTruthy;

window.newPlaceId = ids.newPlaceId;
window.newClientId = ids.newClientId;
window.newReferralId = ids.newReferralId;
window.newApprovalId = ids.newApprovalId;
window.newMHEntryId = ids.newMHEntryId;
window.newStopId = ids.newStopId;

/* Assignment. These decide who can see a record, so they are the one set of
   pure functions in this bundle with a security consequence — hence the tests
   in assign.test.js. Firestore rules are still the enforcement; these only
   have to agree with them. */
window.clientAssignedTo = assign.clientAssignedTo;
window.clientIsAssigned = assign.clientIsAssigned;
window.referralAssignedTo = assign.referralAssignedTo;
window.referralIsAssigned = assign.referralIsAssigned;

/* Attaching Member Hours rows to a client. Pure, and the numbers it produces
   are read as a client's logged time — so it is tested rather than eyeballed,
   and it reports HOW it matched so the UI can be honest about confidence. */
window.normCin = mhMatch.normCin;
window.mhMatchKind = mhMatch.mhMatchKind;
window.mhMatchesClient = mhMatch.mhMatchesClient;
window.mhSumEntries = mhMatch.mhSumEntries;
window.mhMonthsPresent = mhMatch.mhMonthsPresent;
window.mhFilterMonth = mhMatch.mhFilterMonth;

window.normName = text.normName;
window.phoneTail = text.phoneTail;
window.personDedupKey = text.personDedupKey;
/* Also renamed: these are folder-path helpers, nothing to do with notes. */
window.notesFolderDepth = text.folderDepth;
window.notesFolderLeaf = text.folderLeaf;
window.notesFolderIsDescendant = text.folderIsDescendant;

/* A marker the console can check, so "is the bundle actually loaded?" has a
   one-word answer when something looks wrong. */
window.__otterBundle = { built: true, modules: ['lib/time', 'lib/format', 'lib/dates', 'lib/csv', 'lib/ids', 'lib/text', 'lib/assign', 'lib/mh-match', 'lib/ready', 'features/share-dialog', 'features/doc-editor', 'features/notebook', 'features/whiteboard', 'features/ai-panel', 'features/recorder', 'features/meeting-mode'] };
